import type { ElementKey } from '@/lib/experience'
import { ELEMENTS } from '@/lib/experience'
import { motion } from 'framer-motion'
import { useState, useRef } from 'react'

interface Props {
  element: ElementKey
  onContinue: () => void
}

const AUDIO_META: Record<ElementKey, { title: string; label: string; src: string }> = {
  terra: {
    label: 'Meditação Elemento Terra',
    title: 'Prosperidade, Estabilidade e Firmeza',
    src: 'https://www.dropbox.com/scl/fi/b63vz0ujfe4lm7ie4b3pn/Medita-o-Elemento-Terra-M-nica-Maria.mp4?rlkey=y298lmzf0meu3v89r6yb300l8&raw=1',
  },
  agua: {
    label: 'Meditação Elemento Água',
    title: 'Amor, Merecimento e Prazer',
    src: 'https://www.dropbox.com/scl/fi/65xcw2nbbg6oc62ik5zot/Medita-o-Elemento-gua-M-nica-Maria.mp4?rlkey=v9y3k4qz9k957vyeevy0381l2&raw=1',
  },
  fogo: {
    label: 'Meditação Elemento Fogo',
    title: 'Poder, Ação e Realização',
    src: 'https://www.dropbox.com/scl/fi/mdszjdc96iec5gczu8hmb/Medita-o-Elemento-Fogo-M-nica-Maria.mp4?rlkey=02uskianbn3s7lnff3m7u60sd&raw=1',
  },
  ar: {
    label: 'Meditação Elemento Ar',
    title: 'Paz, Equilíbrio e Fluidez',
    src: 'https://www.dropbox.com/scl/fi/ox8br52ogqm65r3tpk7tc/Medita-o-Elemento-Ar-M-nica-Maria.mp4?rlkey=3iaej12123ezbu59788f8cw0t&raw=1',
  },
}

function extractYouTubeId(url: string): string | null {
  if (!url) return null
  const patterns = [
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ]
  for (const pattern of patterns) {
    const match = url.match(pattern)
    if (match) return match[1]
  }
  return null
}

function isSoundCloudUrl(url: string): boolean {
  if (!url) return false
  return /soundcloud\.com\//.test(url)
}

function buildSoundCloudEmbed(url: string, accentHex: string): string {
  // Remove '#' from hex color for SoundCloud param
  const color = accentHex.replace('#', '')
  const encoded = encodeURIComponent(url)
  // Clean player: no artwork/thumbnail, no related/suggestions, no sharing buttons
  return `https://w.soundcloud.com/player/?url=${encoded}&color=%23${color}&auto_play=false&hide_related=true&show_comments=false&show_user=false&show_reposts=false&show_teaser=false&visual=false&show_artwork=false&sharing=false&buying=false&download=false`
}

function WaveformBars({ accent, playing }: { accent: string; playing: boolean }) {
  const bars = [3, 6, 10, 7, 12, 5, 9, 14, 8, 11, 4, 8, 13, 6, 10, 7, 5, 9, 12, 4]
  return (
    <div className="flex items-center justify-center gap-[3px] h-10" aria-hidden>
      {bars.map((h, i) => (
        <span
          key={i}
          className="rounded-full transition-all"
          style={{
            width: 3,
            height: playing ? `${h}px` : '3px',
            background: accent,
            opacity: playing ? 0.75 : 0.3,
            transition: playing
              ? `height ${0.3 + (i % 5) * 0.07}s ease-in-out ${(i % 4) * 0.06}s, opacity 0.4s`
              : 'height 0.4s ease, opacity 0.4s',
            animation: playing ? `waveBar ${0.6 + (i % 4) * 0.15}s ease-in-out ${(i % 5) * 0.05}s infinite alternate` : 'none',
          }}
        />
      ))}
    </div>
  )
}

function StepAudio({ element, onContinue }: Props) {
  const meta = ELEMENTS[element]
  const audio = AUDIO_META[element]
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioRef = useRef<HTMLAudioElement>(null)

  const togglePlay = () => {
    const el = audioRef.current
    if (!el) return
    if (playing) {
      el.pause()
      setPlaying(false)
    } else {
      el.play().catch(() => {})
      setPlaying(true)
    }
  }

  const handleTimeUpdate = () => {
    const el = audioRef.current
    if (!el || !el.duration) return
    setProgress(el.currentTime / el.duration)
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration)
  }

  const handleEnded = () => setPlaying(false)

  const handleScrub = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current
    if (!el) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = (e.clientX - rect.left) / rect.width
    el.currentTime = ratio * el.duration
    setProgress(ratio)
  }

  const fmt = (s: number) => {
    if (!s || !isFinite(s)) return '0:00'
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${m}:${sec.toString().padStart(2, '0')}`
  }

  const hasAudio = !!audio.src
  const youtubeId = extractYouTubeId(audio.src)
  const isYouTube = !!youtubeId
  const isSoundCloud = isSoundCloudUrl(audio.src)
  const soundCloudEmbedUrl = isSoundCloud ? buildSoundCloudEmbed(audio.src, meta.accent) : ''

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-2xl mx-auto"
    >
      {/* Header */}
      <div className="text-center mb-10 md:mb-14">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="text-[10px] tracking-[0.4em] uppercase font-body mb-5"
          style={{ color: meta.accentSoft }}
        >
          Elemento {meta.label} · Etapa <span className="numerals">6</span>
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="font-display type-hero text-[var(--ink)] mb-6"
        >
          Um <em className="italic font-light" style={{ color: meta.accentSoft }}>presente</em> para você
        </motion.h1>

        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 100, opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="h-px mx-auto mb-7"
          style={{
            background: `linear-gradient(90deg, transparent, ${meta.accentSoft}, transparent)`,
          }}
        />

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="font-body italic type-body text-[var(--ink-soft)] max-w-lg mx-auto mb-4"
        >
          Este é um fragmento de meditação criado para o seu elemento. Escute agora com os olhos fechados e permita que cada palavra chegue no seu campo.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="font-body type-body max-w-md mx-auto"
          style={{ color: meta.accentSoft }}
        >
          Depois de ouvir, role para baixo. O próximo passo da sua jornada está te esperando.
        </motion.p>
      </div>

      {/* Player card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.65 }}
        className="card-elevated rounded-sm p-5 sm:p-6 md:p-10 mb-8 relative overflow-hidden"
      >
        {/* Ambient glow */}
        <div
          className="absolute inset-x-0 top-0 h-40 pointer-events-none"
          style={{
            background: `radial-gradient(closest-side, ${meta.accentSoft}18, transparent)`,
          }}
        />

        {/* Track info */}
        <div className="flex items-start gap-3 sm:gap-4 mb-8 relative">
          {/* Element icon */}
          <div
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-sm flex-shrink-0 flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${meta.accent}28, ${meta.accent}0a)`,
              border: `1px solid ${meta.accentSoft}44`,
            }}
          >
            <span className="font-display text-[20px] sm:text-[22px] leading-none" style={{ color: meta.accentSoft }}>
              {meta.symbol}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <p
              className="text-[10px] tracking-[0.3em] sm:tracking-[0.35em] uppercase font-body mb-1 numerals"
              style={{ color: meta.accentSoft }}
            >
              {audio.label}
            </p>
            <p className="font-display type-section" style={{ color: meta.accent }}>
              {audio.title}
            </p>
          </div>
        </div>

        {/* Waveform animation */}
        <div className="mb-6">
          <WaveformBars accent={meta.accentSoft} playing={playing} />
        </div>

        {/* Progress bar */}
        <div
          className="h-[6px] sm:h-1 rounded-full mb-3 cursor-pointer relative overflow-hidden touch-manipulation"
          style={{ background: `${meta.accentSoft}22` }}
          onClick={handleScrub}
          role="slider"
          aria-label="Progresso do áudio"
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-200"
            style={{
              width: `${progress * 100}%`,
              background: `linear-gradient(90deg, ${meta.accent}, ${meta.accentSoft})`,
            }}
          />
          {/* Thumb */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full -translate-x-1/2 transition-all duration-200"
            style={{
              left: `${progress * 100}%`,
              background: meta.accentSoft,
              boxShadow: `0 0 8px ${meta.accentSoft}88`,
            }}
          />
        </div>

        {/* Time */}
        <div className="flex justify-between mb-7">
          <span className="text-[11px] font-body text-[var(--ink-soft)] numerals">
            {fmt(duration * progress)}
          </span>
          <span className="text-[11px] font-body text-[var(--ink-soft)] numerals">
            {fmt(duration)}
          </span>
        </div>

        {/* SoundCloud player (embedded iframe - clean, no thumbnail, no suggestions) */}
        {isSoundCloud ? (
          <div
            className="relative w-full rounded-sm overflow-hidden"
            style={{
              border: `1px solid ${meta.accentSoft}44`,
              boxShadow: `0 8px 32px -8px ${meta.accent}44`,
            }}
          >
            <iframe
              width="100%"
              height="120"
              scrolling="no"
              frameBorder="no"
              allow="autoplay"
              src={soundCloudEmbedUrl}
              title={`${audio.label} - ${audio.title}`}
              style={{ border: 0, display: 'block' }}
            />
          </div>
        ) : isYouTube ? (
          <div
            className="relative w-full rounded-sm overflow-hidden"
            style={{
              aspectRatio: '16 / 9',
              border: `1px solid ${meta.accentSoft}44`,
              boxShadow: `0 8px 32px -8px ${meta.accent}44`,
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}?rel=0&modestbranding=1`}
              title={`${audio.label} - ${audio.title}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              style={{ border: 0 }}
            />
          </div>
        ) : hasAudio ? (
          <div className="flex justify-center">
            <button
              onClick={togglePlay}
              className="relative w-[72px] h-[72px] sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f14]"
              style={{
                background: `linear-gradient(135deg, ${meta.accent}, ${meta.accentSoft})`,
                boxShadow: `0 8px 32px -8px ${meta.accent}88`,
              }}
              aria-label={playing ? 'Pausar meditação' : 'Reproduzir meditação'}
            >
              {playing ? (
                /* Pause icon */
                <span className="flex gap-[5px]">
                  <span className="w-[4px] h-5 rounded-full bg-[#0a0f14]" />
                  <span className="w-[4px] h-5 rounded-full bg-[#0a0f14]" />
                </span>
              ) : (
                /* Play icon */
                <span
                  className="block ml-1"
                  style={{
                    width: 0,
                    height: 0,
                    borderTop: '9px solid transparent',
                    borderBottom: '9px solid transparent',
                    borderLeft: '16px solid #0a0f14',
                  }}
                />
              )}

              {/* Pulse ring when playing */}
              {playing && (
                <span
                  className="absolute inset-0 rounded-full animate-ping opacity-20"
                  style={{ background: meta.accentSoft }}
                />
              )}
            </button>
          </div>
        ) : (
          /* Placeholder when no audio src */
          <div className="flex flex-col items-center gap-3">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center opacity-40"
              style={{
                background: `linear-gradient(135deg, ${meta.accent}55, ${meta.accentSoft}33)`,
                border: `1px solid ${meta.accentSoft}44`,
              }}
            >
              <span
                className="block ml-1"
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '9px solid transparent',
                  borderBottom: '9px solid transparent',
                  borderLeft: `16px solid ${meta.accentSoft}`,
                }}
              />
            </div>
            <p
              className="text-[11px] tracking-[0.25em] uppercase font-body text-center"
              style={{ color: meta.accentSoft }}
            >
              Áudio em breve
            </p>
          </div>
        )}

        {/* Hidden audio element */}
        {hasAudio && !isYouTube && !isSoundCloud && (
          <audio
            ref={audioRef}
            src={audio.src}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
            preload="metadata"
          />
        )}
      </motion.div>

      {/* Below player text + CTA — sempre visível, independente do áudio */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.1 }}
        className="text-center"
      >
        <p className="font-body italic type-body text-[var(--ink-soft)] max-w-md mx-auto mb-10">
          O que você acabou de vivenciar é uma amostra. Existe um caminho mais profundo disponível para você.
        </p>

        <button
          onClick={onContinue}
          className="btn-primary w-full sm:w-auto px-6 sm:px-10 md:px-12 py-4 min-h-[52px] rounded-sm type-button tracking-[0.12em] sm:tracking-[0.15em] uppercase"
        >
          Quero ir mais fundo
        </button>
      </motion.div>
    </motion.div>
  )
}

export default StepAudio
